/* By Layla Saleh try-on v9: perspective-aware fit and synchronized camera frames. */
(function () {
  'use strict';

  var CDN = {
    three:    'https://cdnjs.cloudflare.com/ajax/libs/three.js/r128/three.min.js',
    gltf:     'https://cdn.jsdelivr.net/npm/three@0.128.0/examples/js/loaders/GLTFLoader.js',
    room:     'https://cdn.jsdelivr.net/npm/three@0.128.0/examples/js/environments/RoomEnvironment.js',
    // Pinned deliberately. The prototype used @latest, which silently ships
    // breaking changes to a live storefront.
    mpModule: 'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.12/vision_bundle.mjs',
    mpWasm:   'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.12/wasm',
    mpModel:  'https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task'
  };

  // Millimetre height adjustment; product geometry is never resized.
  var BASE = { heightMm: -2 };
  var S = window.VTONScale;
  if (!S || S.version !== 6) return;

  // MediaPipe canonical face landmark indices
  var NOSE_BRIDGE = 168, TEMPLE_L = 234, TEMPLE_R = 454;

  var scriptCache = {};
  function loadScript(src) {
    if (scriptCache[src]) return scriptCache[src];
    scriptCache[src] = new Promise(function (res, rej) {
      var s = document.createElement('script');
      s.src = src;
      // CORS mode (these CDNs send Access-Control-Allow-Origin: *), so an error
      // thrown inside three.js reaches window.onerror and Clarity with its text
      // and line instead of a bare "Script error.".
      s.crossOrigin = 'anonymous';
      s.async = false;
      s.onload = res;
      s.onerror = function () { delete scriptCache[src]; s.remove(); rej(new Error('Failed to load ' + src)); };
      document.head.appendChild(s);
    });
    return scriptCache[src];
  }

  // Errors caught inside the try-on (so it keeps running) are still reported:
  // once per kind per page, as a Clarity tag next to vton_event / vton_failure.
  var reported = {};
  function vtonErr(where, e) {
    try {
      if (reported[where]) return;
      reported[where] = true;
      var msg = String((e && e.message) || e).slice(0, 100);
      if (window.console && console.warn) console.warn('[vton] ' + where + ': ' + msg);
      if (typeof window.clarity === 'function') window.clarity('set', 'vton_error', where + ': ' + msg);
    } catch (_) {}
  }

  function VTON(root) {
    this.root = root;
    this.modelUrl = root.getAttribute('data-model-url');
    this.productName = root.getAttribute('data-product-title') || 'this frame';
    this.heightMm = BASE.heightMm;
    this.pd = null;
    this.generation = 0;
    this.frameCallback = null;
    this.starting = false;
    this.mirror = true;
    this.stream = null;
    this.raf = null;
    this.booted = false;       // camera up + loops running (per open)
    this.sceneReady = false;   // renderer/scene/model/landmarker built (per page)
    this.running = false;
    this.els = {};
    this.bind();
  }

  VTON.prototype.bind = function () {
    var self = this;
    var openBtn = this.root.querySelector('[data-vton-open]');
    if (openBtn) {
      openBtn.addEventListener('click', function () { self.open(); });
    }
  };

  VTON.prototype.$ = function (sel) { return this.modal.querySelector(sel); };

  VTON.prototype.setStatus = function (msg) {
    if (this.els.status) {
      this.els.status.textContent = msg || '';
      this.els.status.hidden = !msg;
    }
  };

  VTON.prototype.fail = function (title, detail) {
    if (!this.els.gate) return;
    this.els.gate.hidden = false;
    this.els.gateTitle.textContent = title;
    this.els.gateBody.textContent = detail;
    this.els.gateBtn.hidden = true;
    this.setStatus('');
  };

  // ── Open / close ───────────────────────────────────────────────────────
  VTON.prototype.open = function () {
    var self = this;
    if (!this.modal) {
      this.modal = this.root.querySelector('[data-vton-modal]');
      // Reparent to <body>. A position:fixed overlay anchors to the nearest
      // transformed ancestor instead of the viewport, and product sections in
      // this theme animate on scroll — so left in place it would clip.
      if (this.modal) document.body.appendChild(this.modal);
    }
    if (!this.modal) return;
    if (!this.modal.hidden) return;
    this.modal.hidden = false;
    document.body.style.overflow = 'hidden';
    this.lastFocus = document.activeElement;

    if (!this.els.stage) {
      this.els = {
        stage:     this.$('[data-vton-stage]'),
        video:     this.$('[data-vton-video]'),
        bg:        this.$('[data-vton-bg]'),
        c3d:       this.$('[data-vton-3d]'),
        status:    this.$('[data-vton-status]'),
        gate:      this.$('[data-vton-gate]'),
        gateTitle: this.$('[data-vton-gate-title]'),
        gateBody:  this.$('[data-vton-gate-body]'),
        gateBtn:   this.$('[data-vton-gate-btn]'),
        actions:   this.$('[data-vton-actions]'),
        fit:       this.$('[data-vton-fit]'),
        slUd:      this.$('[data-vton-sl-ud]'),
        pd:        this.$('[data-vton-pd]'),
        fitNote:   this.$('[data-vton-fit-note]'),
        fitError:  this.$('[data-vton-fit-error]')
      };
      this.$('[data-vton-close]').addEventListener('click', function () { self.close(); });
      this.$('[data-vton-mirror]').addEventListener('click', function () {
        self.mirror = !self.mirror;
        self.applyMirror();
      });
      this.$('[data-vton-snap]').addEventListener('click', function () { self.snapshot(); });
      // The fit controls are optional. Frame SIZE is deliberately not shopper
      // adjustable: every GLB renders at its true millimetre width, so letting
      // someone scale it would misrepresent how the frame actually fits.
      var adjustBtn = this.$('[data-vton-adjust]');
      if (adjustBtn && this.els.fit) {
        adjustBtn.addEventListener('click', function () {
          self.els.fit.hidden = !self.els.fit.hidden;
        });
      } else if (adjustBtn) {
        adjustBtn.hidden = true;
      }
      this.els.gateBtn.addEventListener('click', function () { self.start(); });
      if (this.els.slUd) this.els.slUd.value = this.heightMm;
      var applyPD = this.$('[data-vton-apply-pd]');
      if (applyPD) applyPD.addEventListener('click', function () {
        var value = self.els.pd.value.trim(), pd = value ? Number(value) : null;
        if (value && (!isFinite(pd) || pd < 45 || pd > 80)) {
          self.els.fitError.textContent = 'Enter a distance PD from 45 to 80 mm, or leave it blank for estimated fit.';
          return;
        }
        self.els.fitError.textContent = '';
        self.pd = pd; self.resetTracking(); self.updateFitNote();
      });
      this.onVisibility = function () { if (document.hidden && !self.modal.hidden) self.close(); };
      this.onPageHide = function () { self.close(); };
      document.addEventListener('visibilitychange',this.onVisibility);
      window.addEventListener('pagehide',this.onPageHide);
      this.onKey = function (e) { if (e.key === 'Escape') self.close(); };
    }
    document.addEventListener('keydown', this.onKey);

    if (!window.isSecureContext) {
      return this.fail('Secure connection required',
        'Camera access needs HTTPS. Open this page over https:// and try again.');
    }
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      return this.fail('Camera not available',
        'This browser does not support camera access. Try Chrome, Safari or Edge.');
    }
    if (!this.modelUrl) {
      return this.fail('No 3D model',
        'This frame does not have a 3D model attached yet.');
    }

    if (this.booted) { this.setStatus('Look at the camera'); return; }

    // Explain before triggering the browser permission prompt. Slamming a
    // permission dialog on a first-time visitor gets it dismissed.
    this.els.gate.hidden = false;
    this.els.gateTitle.textContent = 'Try on ' + this.productName;
    this.els.gateBody.textContent =
      'We use your camera to place the frame on your face. Everything happens ' +
      'on your device. Remove your glasses and look straight ahead briefly to position the frame.';
    this.els.gateBtn.hidden = false;
    this.els.gateBtn.focus();
  };

  VTON.prototype.close = function () {
    ++this.generation;
    this.starting = false;
    if (this.frameCallback !== null && this.els.video && this.els.video.cancelVideoFrameCallback) {
      this.els.video.cancelVideoFrameCallback(this.frameCallback);
    }
    this.frameCallback = null;
    if (this.raf) cancelAnimationFrame(this.raf);
    if (this.assembly) this.assembly.visible = false;
    if (this.occluder) this.occluder.visible = false;
    this.raf = null;
    this.running = false;
    // Release the camera. Without this the indicator light stays on and
    // people notice.
    if (this.stream) {
      this.stream.getTracks().forEach(function (t) { t.stop(); });
      this.stream = null;
    }
    if (this.els.video) this.els.video.srcObject = null;
    // Deliberately NOT resetting sceneReady. The renderer, scene, model and
    // face landmarker stay alive for the life of the page. Rebuilding them on
    // re-open created a second WebGLRenderer on a canvas that already had a
    // GL context; three.js reused the context with stale state caches and
    // every frame came out solid white.
    this.booted = false;
    if (this.modal) this.modal.hidden = true;
    if (this.els.actions) this.els.actions.hidden = true;
    if (this.els.fit) this.els.fit.hidden = true;
    document.body.style.overflow = '';
    document.removeEventListener('keydown', this.onKey);
    if (this.onResize) window.removeEventListener('resize', this.onResize);
    if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus();
  };

  // ── Boot: camera, three.js, model, MediaPipe ───────────────────────────
  VTON.prototype.updateFitNote = function () {
    // Keep measurement controls inside Adjust fit, away from the camera view.
    if (this.els.fitNote) {
      this.els.fitNote.textContent = '';
      this.els.fitNote.hidden = true;
    }
  };

  VTON.prototype.start = function () {
    if (this.starting || this.running || this.modal.hidden) return;
    var self=this, generation=++this.generation;
    this.starting=true;
    this.els.gate.hidden=true;
    this.setStatus('Starting camera…');
    function active() { return generation===self.generation && !self.modal.hidden; }
    function cancelled() { var e=new Error('Cancelled'); e.name='AbortError'; throw e; }
    navigator.mediaDevices.getUserMedia({
      video:{width:{ideal:1280},height:{ideal:720},frameRate:{ideal:30},facingMode:'user'}, audio:false
    }).then(function(stream) {
      if (!active()) { stream.getTracks().forEach(function(t){t.stop();}); cancelled(); }
      self.stream=stream;self.els.video.srcObject=stream;
      return self.els.video.play();
    }).then(function() {
      if (!active()) cancelled();
      if (self.sceneReady) return;
      if (!self.initPromise) {
        self.setStatus('Loading try-on…');
        self.initPromise=loadScript(CDN.three).then(function(){return loadScript(CDN.gltf);})
          .then(function(){return loadScript(CDN.room);})
          .then(function(){if (!self.renderer) self.initScene();return self.loadModel();})
          .then(function(){return self.initMediaPipe();})
          .then(function(){self.sceneReady=true;})
          .catch(function(e){self.initPromise=null;throw e;});
      }
      return self.initPromise;
    }).then(function() {
      if (!active()) cancelled();
      self.starting=false; self.booted=true; self.running=true;
      self.resetTracking();self.resize();self.applyMirror();self.updateFitNote();
      window.addEventListener('resize',self.onResize);
      self.els.actions.hidden=false;
      self.trackLoop(generation);
    }).catch(function(err) {
      if (!active() || err.name==='AbortError') return;
      self.starting=false; self.running=false;
      if (self.stream) self.stream.getTracks().forEach(function(t){t.stop();});
      self.stream=null;self.els.video.srcObject=null;
      if (err.name==='NotAllowedError' || err.name==='SecurityError') {
        self.fail('Camera blocked','Allow camera access for this site in your browser settings, then try again.');
      } else if (err.name==='NotFoundError') {
        self.fail('No camera found','We could not find a camera on this device.');
      } else if (err.name==='NotReadableError') {
        self.fail('Camera in use','Another app is using your camera. Close it and try again.');
      } else { self.fail('Could not start try-on',err.message || String(err)); }
    });
  };

  VTON.prototype.initScene = function () {
    var THREE = window.THREE;
    this.THREE = THREE;
    this.bgCtx = this.els.bg.getContext('2d');

    var renderer = new THREE.WebGLRenderer({
      canvas: this.els.c3d,
      alpha: true,
      antialias: true,
      preserveDrawingBuffer: true
    });
    // The prototype used the raw devicePixelRatio; on a 3x phone that is three
    // times the fragment work for no visible gain. Cap at 2.
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.physicallyCorrectLights = true;
    renderer.outputEncoding = THREE.sRGBEncoding;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.4;
    renderer.setClearColor(0x000000, 0);
    this.renderer = renderer;

    var scene = new THREE.Scene();
    var camera = new THREE.PerspectiveCamera(45, 1, 0.01, 10);
    camera.position.set(0, 0, 0);
    this.scene = scene;
    this.camera = camera;

    scene.add(new THREE.AmbientLight(0xffffff, 0.6));
    scene.add(new THREE.HemisphereLight(0xffffff, 0x444444, 0.6));
    [[-1, 2, 4, 1.4], [2, -1, 2, 0.6], [0, 0, -3, 0.3], [0, 3, 1, 0.2]]
      .forEach(function (p) {
        var d = new THREE.DirectionalLight(0xffffff, p[3]);
        d.position.set(p[0], p[1], p[2]);
        scene.add(d);
      });

    // Studio environment — this is what puts believable highlights on gold
    // and acetate. Without it the frame reads as flat plastic.
    try {
      var pmrem = new THREE.PMREMGenerator(renderer);
      scene.environment = pmrem.fromScene(new THREE.RoomEnvironment(), 0.04).texture;
      pmrem.dispose();
    } catch (e) { /* env map is a nicety, not a requirement */ }

    this.assembly = new THREE.Group();
    scene.add(this.assembly);
    this.frameGroup = new THREE.Group();
    this.assembly.add(this.frameGroup);

    // Face depth follows the detected face independently of the product.
    // Topology: Google MediaPipe canonical_face_model.obj (Apache-2.0).
    var faceIndices=[173,155,133,246,33,7,382,398,362,263,466,249,308,415,324,78,95,191,356,389,264,127,34,162,368,264,389,139,162,34,267,0,302,37,72,0,11,302,0,11,0,72,349,451,350,120,121,231,452,350,451,232,231,121,267,302,269,37,39,72,303,269,302,73,72,39,357,343,350,128,121,114,277,350,343,47,114,121,350,452,357,121,128,232,453,357,452,233,232,128,299,333,297,69,67,104,332,297,333,103,104,67,175,152,396,175,171,152,377,396,152,148,152,171,381,384,382,154,155,157,398,382,384,173,157,155,280,347,330,50,101,118,348,330,347,119,118,101,269,303,270,39,40,73,304,270,303,74,73,40,9,336,151,9,151,107,337,151,336,108,107,151,344,278,360,115,131,48,279,360,278,49,48,131,262,431,418,32,194,211,424,418,431,204,211,194,304,408,270,74,40,184,409,270,408,185,184,40,272,310,407,42,183,80,415,407,310,191,80,183,322,270,410,92,186,40,409,410,270,185,40,186,347,449,348,118,119,229,450,348,449,230,229,119,434,432,430,214,210,212,422,430,432,202,212,210,313,314,18,83,18,84,17,18,314,17,84,18,307,375,306,77,76,146,291,306,375,61,146,76,259,387,260,29,30,160,388,260,387,161,160,30,286,414,384,56,157,190,398,384,414,173,190,157,418,424,406,194,182,204,335,406,424,106,204,182,367,416,364,138,135,192,434,364,416,214,192,135,391,423,327,165,98,203,358,327,423,129,203,98,298,301,284,68,54,71,251,284,301,21,71,54,4,275,5,4,5,45,281,5,275,51,45,5,254,373,253,24,23,144,374,253,373,145,144,23,320,321,307,90,77,91,375,307,321,146,91,77,280,425,411,50,187,205,427,411,425,207,205,187,421,313,200,201,200,83,18,200,313,18,83,200,335,321,406,106,182,91,405,406,321,181,91,182,405,321,404,181,180,91,320,404,321,90,91,180,17,314,16,17,16,84,315,16,314,85,84,16,425,266,426,205,206,36,423,426,266,203,36,206,369,396,400,140,176,171,377,400,396,148,171,176,391,269,322,165,92,39,270,322,269,40,39,92,417,465,413,193,189,245,464,413,465,244,245,189,257,258,386,27,159,28,385,386,258,158,28,159,260,388,467,30,247,161,466,467,388,246,161,247,248,456,419,3,196,236,399,419,456,174,236,196,333,298,332,104,103,68,284,332,298,54,68,103,285,8,417,55,193,8,168,417,8,168,8,193,340,261,346,111,117,31,448,346,261,228,31,117,285,417,441,55,221,193,413,441,417,189,193,221,327,460,326,98,97,240,328,326,460,99,240,97,277,355,329,47,100,126,371,329,355,142,126,100,309,392,438,79,218,166,439,438,392,219,166,218,381,382,256,154,26,155,341,256,382,112,155,26,360,279,420,131,198,49,429,420,279,209,49,198,365,364,379,136,150,135,394,379,364,169,135,150,355,277,437,126,217,47,343,437,277,114,47,217,443,444,282,223,52,224,283,282,444,53,224,52,281,275,363,51,134,45,440,363,275,220,45,134,431,262,395,211,170,32,369,395,262,140,32,170,337,299,338,108,109,69,297,338,299,67,69,109,335,273,321,106,91,43,375,321,273,146,43,91,348,450,349,119,120,230,451,349,450,231,230,120,467,359,342,247,113,130,446,342,359,226,130,113,282,283,334,52,105,53,293,334,283,63,53,105,250,458,462,20,242,238,461,462,458,241,238,242,276,353,300,46,70,124,383,300,353,156,124,70,325,292,324,96,95,62,308,324,292,78,62,95,283,276,293,53,63,46,300,293,276,70,46,63,447,264,345,227,116,34,372,345,264,143,34,116,352,345,346,123,117,116,340,346,345,111,116,117,1,19,274,1,44,19,354,274,19,125,19,44,248,281,456,3,236,51,363,456,281,134,51,236,425,426,427,205,207,206,436,427,426,216,206,207,380,381,252,153,22,154,256,252,381,26,154,22,391,393,269,165,39,167,267,269,393,37,167,39,199,428,200,199,200,208,421,200,428,201,208,200,330,329,266,101,36,100,371,266,329,142,100,36,422,432,273,202,43,212,287,273,432,57,212,43,290,250,328,60,99,20,462,328,250,242,20,99,258,286,385,28,158,56,384,385,286,157,56,158,342,446,353,113,124,226,265,353,446,35,226,124,257,386,259,27,29,159,387,259,386,160,159,29,430,422,431,210,211,202,424,431,422,204,202,211,445,342,276,225,46,113,353,276,342,124,113,46,424,422,335,204,106,202,273,335,422,43,202,106,306,292,307,76,77,62,325,307,292,96,62,77,366,447,352,137,123,227,345,352,447,116,227,123,302,268,303,72,73,38,271,303,268,41,38,73,371,358,266,142,36,129,423,266,358,203,129,36,327,294,460,98,240,64,455,460,294,235,64,240,294,331,278,64,48,102,279,278,331,49,102,48,303,271,304,73,74,41,272,304,271,42,41,74,427,436,434,207,214,216,432,434,436,212,216,214,304,272,408,74,184,42,407,408,272,183,42,184,394,430,395,169,170,210,431,395,430,211,210,170,395,369,378,170,149,140,400,378,369,176,140,149,296,334,299,66,69,105,333,299,334,104,105,69,417,168,351,193,122,168,6,351,168,6,168,122,280,411,352,50,123,187,376,352,411,147,187,123,319,320,325,89,96,90,307,325,320,77,90,96,285,295,336,55,107,65,296,336,295,66,65,107,404,320,403,180,179,90,319,403,320,89,90,179,330,348,329,101,100,119,349,329,348,120,119,100,334,293,333,105,104,63,298,333,293,68,63,104,323,454,366,93,137,234,447,366,454,227,234,137,16,315,15,16,15,85,316,15,315,86,85,15,429,279,358,209,129,49,331,358,279,102,49,129,15,316,14,15,14,86,317,14,316,87,86,14,8,285,9,8,9,55,336,9,285,107,55,9,329,349,277,100,47,120,350,277,349,121,120,47,252,253,380,22,153,23,374,380,253,145,23,153,402,403,318,178,88,179,319,318,403,89,179,88,351,6,419,122,196,6,197,419,6,197,6,196,324,318,325,95,96,88,319,325,318,89,88,96,397,367,365,172,136,138,364,365,367,135,138,136,288,435,397,58,172,215,367,397,435,138,215,172,438,439,344,218,115,219,278,344,439,48,219,115,271,311,272,41,42,81,310,272,311,80,81,42,5,281,195,5,195,51,248,195,281,3,51,195,273,287,375,43,146,57,291,375,287,61,57,146,396,428,175,171,175,208,199,175,428,199,208,175,268,312,271,38,41,82,311,271,312,81,82,41,444,445,283,224,53,225,276,283,445,46,225,53,254,339,373,24,144,110,390,373,339,163,110,144,295,282,296,65,66,52,334,296,282,105,52,66,346,448,347,117,118,228,449,347,448,229,228,118,454,356,447,234,227,127,264,447,356,34,127,227,336,296,337,107,108,66,299,337,296,69,66,108,151,337,10,151,10,108,338,10,337,109,108,10,278,439,294,48,64,219,455,294,439,235,219,64,407,415,292,183,62,191,308,292,415,78,191,62,358,371,429,129,209,142,355,429,371,126,142,209,345,372,340,116,111,143,265,340,372,35,143,111,388,390,466,161,246,163,249,466,390,7,163,246,352,346,280,123,50,117,347,280,346,118,117,50,295,442,282,65,52,222,443,282,442,223,222,52,19,94,354,19,125,94,370,354,94,141,94,125,295,285,442,65,222,55,441,442,285,221,55,222,419,197,248,196,3,197,195,248,197,195,197,3,359,263,255,130,25,33,249,255,263,7,33,25,275,274,440,45,220,44,457,440,274,237,44,220,300,383,301,70,71,156,368,301,383,139,156,71,417,351,465,193,245,122,412,465,351,188,122,245,466,263,467,246,247,33,359,467,263,130,33,247,389,251,368,162,139,21,301,368,251,71,21,139,374,386,380,145,153,159,385,380,386,158,159,153,379,394,378,150,149,169,395,378,394,170,169,149,351,419,412,122,188,196,399,412,419,174,196,188,426,322,436,206,216,92,410,436,322,186,92,216,387,373,388,160,161,144,390,388,373,163,144,161,393,326,164,167,164,97,2,164,326,2,97,164,354,370,461,125,241,141,462,461,370,242,141,241,0,267,164,0,164,37,393,164,267,167,37,164,11,12,302,11,72,12,268,302,12,38,12,72,386,374,387,159,160,145,373,387,374,144,145,160,12,13,268,12,38,13,312,268,13,82,13,38,293,300,298,63,68,70,301,298,300,71,70,68,340,265,261,111,31,35,446,261,265,226,35,31,380,385,381,153,154,158,384,381,385,157,158,154,280,330,425,50,205,101,266,425,330,36,101,205,423,391,426,203,206,165,322,426,391,92,165,206,429,355,420,209,198,126,437,420,355,217,126,198,391,327,393,165,167,98,326,393,327,97,98,167,457,438,440,237,220,218,344,440,438,115,218,220,382,362,341,155,112,133,463,341,362,243,133,112,457,461,459,237,239,241,458,459,461,238,241,239,434,430,364,214,135,210,394,364,430,169,210,135,414,463,398,190,173,243,362,398,463,133,243,173,262,428,369,32,140,208,396,369,428,171,208,140,457,274,461,237,241,44,354,461,274,125,44,241,316,403,317,86,87,179,402,317,403,178,179,87,315,404,316,85,86,180,403,316,404,179,180,86,314,405,315,84,85,181,404,315,405,180,181,85,313,406,314,83,84,182,405,314,406,181,182,84,418,406,421,194,201,182,313,421,406,83,182,201,366,401,323,137,93,177,361,323,401,132,177,93,408,407,306,184,76,183,292,306,407,62,183,76,408,306,409,184,185,76,291,409,306,61,76,185,410,409,287,186,57,185,291,287,409,61,185,57,436,410,432,216,212,186,287,432,410,57,186,212,434,416,427,214,207,192,411,427,416,187,192,207,264,368,372,34,143,139,383,372,368,156,139,143,457,459,438,237,218,239,309,438,459,79,239,218,352,376,366,123,137,147,401,366,376,177,147,137,4,1,275,4,45,1,274,275,1,44,1,45,428,262,421,208,201,32,418,421,262,194,32,201,327,358,294,98,64,129,331,294,358,102,129,64,367,435,416,138,192,215,433,416,435,213,215,192,455,439,289,235,59,219,392,289,439,166,219,59,328,462,326,99,97,242,370,326,462,141,242,97,326,370,2,97,2,141,94,2,370,94,141,2,460,455,305,240,75,235,289,305,455,59,235,75,448,339,449,228,229,110,254,449,339,24,110,229,261,446,255,31,25,226,359,255,446,130,226,25,449,254,450,229,230,24,253,450,254,23,24,230,450,253,451,230,231,23,252,451,253,22,23,231,451,252,452,231,232,22,256,452,252,26,22,232,256,341,452,26,232,112,453,452,341,233,112,232,413,464,414,189,190,244,463,414,464,243,244,190,441,413,286,221,56,189,414,286,413,190,189,56,441,286,442,221,222,56,258,442,286,28,56,222,442,258,443,222,223,28,257,443,258,27,28,223,444,443,259,224,29,223,257,259,443,27,223,29,259,260,444,29,224,30,445,444,260,225,30,224,260,467,445,30,225,247,342,445,467,113,247,225,250,309,458,20,238,79,459,458,309,239,79,238,290,305,392,60,166,75,289,392,305,59,75,166,460,305,328,240,99,75,290,328,305,60,75,99,376,433,401,147,177,213,435,401,433,215,213,177,250,290,309,20,79,60,392,309,290,166,60,79,411,416,376,187,147,192,433,376,416,213,192,147,341,463,453,112,233,243,464,453,463,244,243,233,453,464,357,233,128,244,465,357,464,245,244,128,412,343,465,188,245,114,357,465,343,128,114,245,437,343,399,217,174,114,412,399,343,188,114,174,363,440,360,134,131,220,344,360,440,115,220,131,456,420,399,236,174,198,437,399,420,217,198,174,456,363,420,236,198,134,360,420,363,131,134,198,361,401,288,132,58,177,435,288,401,215,177,58,353,265,383,124,156,35,372,383,265,143,35,156,255,249,339,25,110,7,390,339,249,163,7,110,261,255,448,31,228,25,339,448,255,110,25,228,14,317,13,14,13,87,312,13,317,82,87,13,317,402,312,87,82,178,311,312,402,81,178,82,402,318,311,178,81,88,310,311,318,80,88,81,318,324,310,88,80,95,415,310,324,191,95,80,33,7,163,33,163,144,33,144,145,33,145,153,33,153,154,33,154,155,33,155,133,33,133,173,33,173,157,33,157,158,33,158,159,33,159,160,33,160,161,33,161,246,263,249,390,263,390,373,263,373,374,263,374,380,263,380,381,263,381,382,263,382,362,263,362,398,263,398,384,263,384,385,263,385,386,263,386,387,263,387,388,263,388,466];
    var geo=new THREE.BufferGeometry();
    geo.setAttribute('position',new THREE.BufferAttribute(new Float32Array(468*3),3));
    geo.setIndex(faceIndices);
    var depthMaterial=new THREE.MeshBasicMaterial({colorWrite:false,depthWrite:true,depthTest:true,side:THREE.DoubleSide});
    // The face model ends at the facial oval. A closed cranial volume and
    // conservative ear volumes cover the parts of the temples behind it.
    // They are fitted to the person's landmarks, never to the glasses width.
    var maskRig=new THREE.Group();maskRig.visible=false;
    var mask=new THREE.Mesh(geo,depthMaterial);
    mask.renderOrder=-10;mask.frustumCulled=false;
    maskRig.add(mask);this.faceOccluder=mask;
    var sphere=new THREE.SphereGeometry(1,40,28);
    this.headOccluder=new THREE.Mesh(sphere,depthMaterial);
    this.earOccluders=[new THREE.Mesh(sphere,depthMaterial),new THREE.Mesh(sphere,depthMaterial)];
    [this.headOccluder].concat(this.earOccluders).forEach(function(o){o.renderOrder=-10;o.frustumCulled=false;maskRig.add(o);});
    scene.add(maskRig);this.occluder=maskRig;
    this.faceLocal=Array.from({length:468},function(){return new THREE.Vector3();});
    this.assembly.visible=false;

    this.targetPos = new THREE.Vector3();
    this.targetScale = new THREE.Vector3(1, 1, 1);
    this.targetQuat = new THREE.Quaternion();
    this.prevTargetQuat = new THREE.Quaternion();
    this.velPos = new THREE.Vector3();
    this.resetTracking();

    var self = this;
    this.onResize = function () { self.resize(); };
    this.resize();
    this.applyMirror();
  };

  // Fresh filters and no remembered pose, so the first frames after (re)open
  // snap to the face instead of easing over from wherever it was last time.
  VTON.prototype.resetTracking = function () {
    this.calibration=new S.Calibration(this.pd);
    this.filters={px:new S.OneEuro(3,0.045),py:new S.OneEuro(3,0.045),scale:new S.OneEuro(3,2)};
    this.headShape=null;this.bridgeClearance=null;
    this.lastTrackTime=0;this.hasTarget=false;this.lastFilteredQ=null;this.lastVideoTime=-1;this.noFaceSince=null;
    if (this.assembly) this.assembly.visible=false;
    if (this.occluder) this.occluder.visible=false;
    this.setStatus('Look straight ahead for a moment');
  };

  VTON.prototype.resize = function () {
    var r = this.els.stage.getBoundingClientRect();
    // Fall back to the viewport if the stage has not laid out yet. A zero-size
    // drawing buffer renders nothing and reports no error at all, so never let
    // a measurement of 0 through.
    var W = Math.max(1, Math.round(r.width) || window.innerWidth || 1);
    var H = Math.max(1, Math.round(r.height) || window.innerHeight || 1);
    this.W = W;
    this.H = H;
    this.renderer.setSize(W, H, false);
    this.els.bg.width = W;
    this.els.bg.height = H;
    this.camera.aspect = W / H;
    this.camera.updateProjectionMatrix();
  };

  VTON.prototype.applyMirror = function () {
    this.els.bg.classList.toggle('vton-mirrored', this.mirror);
    this.els.c3d.classList.toggle('vton-mirrored', this.mirror);
  };

  VTON.prototype.loadModel = function () {
    var self = this, THREE = this.THREE;
    this.setStatus('Loading frame…');
    return new Promise(function (resolve, reject) {
      new THREE.GLTFLoader().load(self.modelUrl, function (gltf) {
        var m = gltf.scene;
        var box = new THREE.Box3().setFromObject(m);
        var size = box.getSize(new THREE.Vector3());
        var cen = box.getCenter(new THREE.Vector3());

        if (!(size.x >= 0.10 && size.x <= 0.18)) {
          reject(new Error('This frame needs a model exported in metres.'));return;
        }
        // Never normalise or resize the product. The drawing-based model
        // coordinates are metres, with the bridge centred around Y=0.
        // Derive the bridge datum from the front mesh, rather than its bbox
        // midpoint (which includes the low-hanging temple tips).
        var bridgeY=0, bridgeZ=box.max.z;
        m.traverse(function(o) {
          if (!o.isMesh || !/^mainframe/i.test(o.name || '')) return;
          var a=o.geometry.attributes.position, low=Infinity, high=-Infinity, z=-Infinity;
          for (var i=0;i<a.count;i++) if (Math.abs(a.getX(i)-cen.x)<0.001) {
            low=Math.min(low,a.getY(i));high=Math.max(high,a.getY(i));z=Math.max(z,a.getZ(i));
          }
          if (isFinite(low)) {bridgeY=(low+high)/2;bridgeZ=z;}
        });
        m.position.sub(new THREE.Vector3(cen.x,bridgeY,bridgeZ));
        self.modelWidthMetres=size.x;
        // Contact samples include the rear and lower surfaces of the bridge.
        // Anchoring its FRONT surface on landmark 168 embeds 2–5 mm of frame
        // in the nose before accounting for the nose's slope below that point.
        self.bridgeContact=[];
        m.traverse(function(o){
          if(!o.isMesh || !/^mainframe/i.test(o.name||'')) return;
          var a=o.geometry.attributes.position;
          for(var i=0;i<a.count;i++) if(Math.abs(a.getX(i)-cen.x)<0.003) {
            self.bridgeContact.push({y:a.getY(i)-bridgeY,z:a.getZ(i)-bridgeZ});
          }
        });
        if(!self.bridgeContact.length) self.bridgeContact=[{y:0,z:-0.005}];

        m.traverse(function (c) {
          if (!c.isMesh || !c.material) return;
          var mats = [].concat(c.material).map(function (mat) {
            // GLBs exported with KHR_materials_clearcoat come in as
            // MeshPhysicalMaterial. On phones and weak integrated GPUs that
            // shader overflows in r128 and the whole frame renders white.
            // MeshStandardMaterial looks the same at this size and renders
            // correctly everywhere, so downgrade and drop the clearcoat.
            if (mat.isMeshPhysicalMaterial) {
              var std = new THREE.MeshStandardMaterial();
              std.name = mat.name;
              std.color.copy(mat.color);
              std.roughness = mat.roughness;
              std.metalness = mat.metalness;
              std.opacity = mat.opacity;
              // KHR_materials_transmission = glass. Standard has no transmission,
              // so a transmissive part would come out as solid colour (usually
              // white — the exporter leaves base colour at default). Map it to
              // plain alpha instead: fully transmissive -> nearly clear.
              if (mat.transmission > 0) {
                std.opacity = Math.min(std.opacity, 1 - mat.transmission * 0.85);
              }
              std.side = mat.side;
              std.alphaTest = mat.alphaTest;
              std.map = mat.map;
              std.normalMap = mat.normalMap;
              std.roughnessMap = mat.roughnessMap;
              std.metalnessMap = mat.metalnessMap;
              std.emissive.copy(mat.emissive);
              std.emissiveMap = mat.emissiveMap;
              // A glTF lens with alphaMode BLEND and the opacity in its
              // texture's alpha channel (no baseColorFactor) arrives as
              // transparent = true, opacity = 1. Without copying the flag the
              // test below reads (false || 1 < 0.99) -> false, blending stays
              // off, and the lens composites additively over the webcam frame:
              // on pale skin it blows out to solid white. Only the three
              // gradient lenses are built that way; every other material
              // already qualifies via opacity < 0.99, so this is additive.
              std.transparent = mat.transparent;
              mat = std;
            }
            // Only blend where the material is genuinely translucent. This used
            // to force transparent/depthWrite on EVERYTHING, which broke depth
            // sorting on solid acetate: the far temple drew on top of the front
            // of the frame and the frame read as see-through.
            var translucent = mat.transparent === true || mat.opacity < 0.99;
            mat.transparent = translucent;
            mat.depthWrite = !translucent;
            var lens=/\blens\b/i.test(mat.name || '') && !/laser|mark/i.test(mat.name || '');
            // 2026-10-15: the four dark smoke lenses were calibrated against
            // a white product-photo background. That looked pale blue over a
            // webcam face. Reduce their diffuse lift and face show-through only;
            // clear, gradient, amber lenses and laser marks keep their materials.
            var darkSmoke = lens && /^(JS-4-C1|LS-00-C1|JS-2-C1|JS-5B-C1) black lens$/i.test(mat.name || '');
            if (darkSmoke) {
              var neutral = (mat.color.r + mat.color.g + mat.color.b) / 3;
              mat.color.setRGB(
                (mat.color.r * 0.6 + neutral * 0.4) * 0.35,
                (mat.color.g * 0.6 + neutral * 0.4) * 0.35,
                (mat.color.b * 0.6 + neutral * 0.4) * 0.35
              );
              mat.opacity = Math.max(mat.opacity, 0.90);
              mat.transparent = true;
              mat.depthWrite = false;
            }
            mat.envMapIntensity = darkSmoke ? 0.18 : (lens ? 0.45 : 1.5);
            if(lens && mat.isMeshStandardMaterial) {
              // Keep the inexpensive Standard shader while avoiding the hard
              // studio-light spots that obscured the eye in the camera view.
              // Base colour, lens opacity and all product dimensions stay intact.
              mat.onBeforeCompile=function(shader) {
                shader.fragmentShader=shader.fragmentShader.replace('#include <lights_physical_fragment>',
                  '#include <lights_physical_fragment>\nmaterial.specularColor *= 0.16;');
              };
              mat.customProgramCacheKey=function(){return 'vton-soft-lens-v1';};
            }
            mat.needsUpdate = true;
            return mat;
          });
          c.material = mats.length === 1 ? mats[0] : mats;
        });
        self.frameGroup.clear();
        self.frameGroup.add(m);
        self.assembly.scale.setScalar(1);
        resolve();
      }, undefined, function () {
        reject(new Error('Could not load the 3D model for this frame.'));
      });
    });
  };

  // Shopify's asset pipeline minifies and transpiles everything under assets/,
  // and it downlevels a bare dynamic import() into require() — which does not
  // exist in a browser, so the try-on died with "require is not defined".
  // Building the import inside an inline module script keeps it as a *string*
  // in this file, so the transpiler cannot see it, let alone rewrite it.
  function importModule(url) {
    return new Promise(function (resolve, reject) {
      var key = '__vtonMP_' + Math.random().toString(36).slice(2);
      var done = false, timer;

      window[key] = function (mod, err) {
        if (done) return;
        done = true; clearTimeout(timer); if (s) s.remove();
        try { delete window[key]; } catch (e) { window[key] = undefined; }
        if (mod) resolve(mod); else reject(err || new Error('Module failed to load'));
      };

      var s = document.createElement('script');
      s.type = 'module';
      s.textContent =
        'import(' + JSON.stringify(url) + ')' +
        '.then(function(m){if(window[' + JSON.stringify(key) + '])window[' + JSON.stringify(key) + '](m);})' +
        '.catch(function(e){if(window[' + JSON.stringify(key) + '])window[' + JSON.stringify(key) + '](null,e);});';
      document.head.appendChild(s);

      timer = setTimeout(function () {
        if (!done) {
          done = true; s.remove(); try { delete window[key]; } catch(e) { window[key] = undefined; }
          reject(new Error('Face tracking took too long to load. Check your connection.'));
        }
      }, 30000);
    });
  }

  VTON.prototype.initMediaPipe = function () {
    var self = this;
    return importModule(CDN.mpModule).then(function (mod) {
      return mod.FilesetResolver.forVisionTasks(CDN.mpWasm).then(function (vision) {
        return mod.FaceLandmarker.createFromOptions(vision, {
          baseOptions: { modelAssetPath: CDN.mpModel, delegate: 'GPU' },
          runningMode: 'VIDEO',
          numFaces: 1,
          outputFacialTransformationMatrixes: true
        });
      });
    }).then(function (fl) { self.faceLandmarker = fl; });
  };

  // Nose surface in the tracked head's local coordinates. Samples run from
  // the brow down the nasal bridge; interpolation follows height adjustment.
  VTON.prototype.noseSurfaceAt = function (y) {
    var points=[8,168,6,197,195,5].map(function(i){return this.faceLocal[i];},this);
    points.sort(function(a,b){return a.y-b.y;});
    if(y<=points[0].y) return points[0].z;
    for(var i=1;i<points.length;i++) if(y<=points[i].y) {
      var a=points[i-1],b=points[i],t=(y-a.y)/Math.max(0.000001,b.y-a.y);
      return a.z+(b.z-a.z)*t;
    }
    return points[points.length-1].z;
  };

  VTON.prototype.fitHeadOccluders = function () {
    var p=this.faceLocal;
    if(!this.headShape) {
      var half=Math.max(Math.abs(p[127].x),Math.abs(p[356].x),Math.abs(p[234].x),Math.abs(p[454].x));
      half=Math.max(0.045,Math.min(0.100,half));
      var unit=half/0.077;
      this.headShape={half:half,unit:unit,z:(p[127].z+p[356].z)/2,y:(p[127].y+p[356].y)/2};
    }
    var h=this.headShape,u=h.unit;
    // Keep the cranial cap BEHIND the nasal plane even when the monocular
    // estimate reports a broad, shallow face. Otherwise the cranium itself
    // incorrectly erases the bridge and lenses during a head tilt.
    var front=-0.028*u,back=h.z-0.135*u;
    this.headOccluder.position.set(0,h.y-0.010*u,(front+back)/2);
    this.headOccluder.scale.set(h.half*1.02,h.half*1.40,(front-back)/2);
    this.earOccluders.forEach(function(o,i){
      o.position.set((i===0?-1:1)*(h.half+0.003*u),h.y-0.020*u,h.z-0.028*u);
      o.scale.set(0.011*u,0.026*u,0.029*u);
    });
  };

  VTON.prototype.trackFace = function (lm, fm, time) {
    var THREE=this.THREE,v=this.els.video,vw=v.videoWidth,vh=v.videoHeight;
    var m=S.measure(lm,fm,vw,vh),pxPerMm=this.calibration.update(m,time);
    if(!pxPerMm) return false;
    var crop=S.cover(this.W,this.H,vw,vh),focal=vw/(2*Math.tan(Math.PI/6));
    var fs=Math.exp(this.filters.scale.filter(Math.log(pxPerMm),time));
    var eyeDepth=focal/fs/1000,nb=lm[NOSE_BRIDGE];
    var bridgeDepth=eyeDepth+(nb.z-m.eyeZ)*vw/fs/1000;
    if(!(bridgeDepth>0.10 && bridgeDepth<2.5)) return false;
    var rawOrigin=new THREE.Vector3((nb.x*vw-vw/2)*bridgeDepth/focal,-(nb.y*vh-vh/2)*bridgeDepth/focal,-bridgeDepth);
    var mat=new THREE.Matrix4().fromArray(fm),rawQ=new THREE.Quaternion();
    mat.decompose(new THREE.Vector3(),rawQ,new THREE.Vector3());rawQ.normalize();
    var invQ=rawQ.clone().invert();
    var a=this.faceOccluder.geometry.attributes.position;
    // Reconstruct in the raw pose, then render both the mask and the glasses
    // with ONE filtered pose. Raw mask + filtered glasses causes nasal cuts
    // and slipping occlusion boundaries during a turn.
    for(var i=0;i<468;i++) {
      var p=lm[i];if(!p || !isFinite(p.x+p.y+p.z)) return false;
      var z=eyeDepth+(p.z-m.eyeZ)*vw/fs/1000;
      var local=this.faceLocal[i].set((p.x*vw-vw/2)*z/focal,-(p.y*vh-vh/2)*z/focal,-z).sub(rawOrigin).applyQuaternion(invQ);
      a.setXYZ(i,local.x,local.y,local.z);
    }
    a.needsUpdate=true;
    var x=this.filters.px.filter(nb.x*vw,time),y=this.filters.py.filter(nb.y*vh,time);
    var pos=new THREE.Vector3((x-vw/2)*bridgeDepth/focal,-(y-vh/2)*bridgeDepth/focal,-bridgeDepth);
    var q=rawQ.clone(),dt=Math.min(0.1,Math.max(0.001,time-this.lastTrackTime));
    if(this.lastFilteredQ) {
      var speed=this.lastFilteredQ.angleTo(q)/dt,alpha=1-Math.exp(-2*Math.PI*(5+1.5*speed)*dt);
      q=this.lastFilteredQ.clone().slerp(q,alpha);
    }
    this.lastFilteredQ=q.clone();this.lastTrackTime=time;
    this.occluder.position.copy(pos);this.occluder.quaternion.copy(q);
    this.fitHeadOccluders();
    var height=this.els.slUd?Number(this.els.slUd.value):this.heightMm;
    height=(isFinite(height)?height:0)/1000;
    var clearance=0.0015;
    this.bridgeContact.forEach(function(p){clearance=Math.max(clearance,this.noseSurfaceAt(p.y+height)-p.z+0.0015);},this);
    // Move immediately out of any collision, relax slowly toward the nose.
    // Only translation changes. Dimensions, temple length and width stay fixed.
    if(this.bridgeClearance===null) this.bridgeClearance=clearance;
    else this.bridgeClearance=Math.max(clearance,this.bridgeClearance+(clearance-this.bridgeClearance)*(1-Math.exp(-dt*8)));
    this.assembly.position.copy(pos).add(new THREE.Vector3(0,height,this.bridgeClearance).applyQuaternion(q));
    this.assembly.quaternion.copy(q);this.assembly.scale.setScalar(1);
    this.camera.fov=2*Math.atan(this.H/(2*focal*crop.scale))*180/Math.PI;
    this.camera.aspect=this.W/this.H;this.camera.updateProjectionMatrix();
    this.hasTarget=true;return true;
  };

  VTON.prototype.trackLoop = function (generation) {
    var self=this;
    function step() {
      if (!self.running || generation!==self.generation) return;
      var v=self.els.video;
      if (v.readyState>=2 && v.videoWidth && v.videoHeight && v.currentTime!==self.lastVideoTime) { try {
        self.lastVideoTime=v.currentTime;
        // Freeze the decoded frame once. A video element may advance in the
        // media thread between painting the background and running inference.
        // Both tasks now consume the same immutable canvas for this step.
        var cameraFrame=self.captureVideoFrame();
        self.drawVideoCover(cameraFrame);
        var ok=false, sawFace=false, now=performance.now()/1000;
        try {
          var res=self.faceLandmarker.detectForVideo(cameraFrame,now*1000);
          if (res.faceLandmarks && res.faceLandmarks.length &&
              res.facialTransformationMatrixes && res.facialTransformationMatrixes.length) {
            sawFace=true;
            ok=self.trackFace(res.faceLandmarks[0],res.facialTransformationMatrixes[0].data,now);
          }
        } catch(e) { /* Hide a dropped/unusable pose; never leave a floating frame. */ }
        if (sawFace) self.noFaceSince=null;
        else if (self.noFaceSince === null || self.noFaceSince === undefined) self.noFaceSince=now;
        else if (now-self.noFaceSince>1.5 && self.calibration.cornerMm) {
          self.resetTracking(); self.noFaceSince=now;
        }
        self.assembly.visible=ok;self.occluder.visible=ok;
        if (!ok && self.lastTrackTime && now-self.lastTrackTime>0.25) {
          self.lastFilteredQ=null;
          Object.keys(self.filters).forEach(function(k){self.filters[k].reset();});
        }
        self.setStatus(ok ? '' : (self.calibration.cornerMm ? 'Look toward the camera' : 'Look straight ahead for a moment'));
        self.renderer.render(self.scene,self.camera);
      } catch (e) { vtonErr('loop', e); } }
      if (v.requestVideoFrameCallback) self.frameCallback=v.requestVideoFrameCallback(step);
      else self.raf=requestAnimationFrame(step);
    }
    step();
  };

  VTON.prototype.captureVideoFrame = function () {
    var v=this.els.video;
    if(!this.cameraFrame) {
      this.cameraFrame=document.createElement('canvas');
      this.cameraFrameCtx=this.cameraFrame.getContext('2d');
    }
    if(this.cameraFrame.width!==v.videoWidth || this.cameraFrame.height!==v.videoHeight) {
      this.cameraFrame.width=v.videoWidth;this.cameraFrame.height=v.videoHeight;
      this.lastFilteredQ=null;
      Object.keys(this.filters).forEach(function(k){this.filters[k].reset();},this);
    }
    this.cameraFrameCtx.drawImage(v,0,0,this.cameraFrame.width,this.cameraFrame.height);
    return this.cameraFrame;
  };

  VTON.prototype.drawVideoCover = function (frame) {
    var v=this.els.video,crop=S.cover(this.W,this.H,v.videoWidth,v.videoHeight);
    this.bgCtx.clearRect(0,0,this.W,this.H);
    this.bgCtx.drawImage(frame || v,crop.x,crop.y,crop.width,crop.height);
  };

  VTON.prototype.snapshot = function () {
    var self = this;
    try {
    this.renderer.render(this.scene, this.camera);
    var out = document.createElement('canvas');
    out.width = this.els.bg.width;
    out.height = this.els.bg.height;
    var ctx = out.getContext('2d');
    if (this.mirror) { ctx.translate(out.width, 0); ctx.scale(-1, 1); }
    ctx.drawImage(this.els.bg, 0, 0, out.width, out.height);
    ctx.drawImage(this.els.c3d, 0, 0, out.width, out.height);
    if (this.mirror) ctx.setTransform(1, 0, 0, 1, 0, 0);
    out.toBlob(function (blob) {
      if (!blob) { vtonErr('snapshot', 'empty image'); self.setStatus('Could not save'); return; }
      var url = URL.createObjectURL(blob);
      var a = document.createElement('a');
      a.href = url;
      a.download = 'by-layla-saleh-tryon-' + Date.now() + '.png';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(function () { URL.revokeObjectURL(url); }, 5000);
      self.setStatus('Saved');
      setTimeout(function () { self.setStatus(''); }, 1600);
    }, 'image/png');
    } catch (e) { vtonErr('snapshot', e); self.setStatus('Could not save'); }
  };

  function init() {
    var roots = document.querySelectorAll('[data-vton-root]');
    for (var i = 0; i < roots.length; i++) {
      if (!roots[i].__vton) roots[i].__vton = new VTON(roots[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();
